import { useEffect, useState } from "react";
import { activate } from "../../../utils/keyboard-activate";
import {
  getPendingSubmissions,
  dismissSubmission,
  resolveSubmission,
} from "../../../services/crmSubmissions";
import { useAuth } from "../../../hooks/useAuth";
import type { PendingSubmissionResponse } from "../../../types/submissions";
import { ResolveSubmissionModal } from "./ResolveSubmissionModal";
import type { ResolveSubmissionPayload } from "./ResolveSubmissionModal";
import {
  MAX_RESTORED_PAGES,
  loadCrmViewState,
  pendingSubmissionsScrollKey,
  pendingSubmissionsViewKey,
  readPageParam,
  saveCrmViewState,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./PendingSubmissionsPage.css";

const readStoredPage = (): number =>
  Math.min(
    readPageParam(loadCrmViewState(pendingSubmissionsViewKey())?.get("page") ?? null),
    MAX_RESTORED_PAGES
  );

interface SubmittedParent {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  slackUsername?: string;
  xHandle?: string;
}

interface SubmittedPlayerData {
  firstName?: string;
  lastName?: string;
  gradYear?: number;
  position?: string;
  email?: string;
  phone?: string;
  bats?: string;
  throws?: string;
  height?: string;
  weight?: number;
  school?: string;
  videoLink?: string;
  scoutingProfileLink?: string;
  city?: string;
  state?: string;
  gpa?: string;
  intendedMajor?: string;
  preferredLocations?: string[];
  travelTeam?: string;
  travelTeamCoachName?: string;
  travelTeamCoachPhone?: string;
  recruitmentStage?: string;
  endGoal?: string;
  notes?: string;
  parent1?: SubmittedParent;
  parent2?: SubmittedParent;
}

const parseSubmittedData = (json: string): SubmittedPlayerData => {
  try {
    return JSON.parse(json) as SubmittedPlayerData;
  } catch {
    return {};
  }
};

const formatPhone = (phone?: string) => {
  if (!phone) return undefined;
  const d = phone.replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return phone;
};

export const PendingSubmissionsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<PendingSubmissionResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [resolvingSubmission, setResolvingSubmission] = useState<PendingSubmissionResponse | null>(null);
  const [isRestored, setIsRestored] = useState(false);

  const publicSubmissionToken = user?.profile?.publicSubmissionToken;
  const submissionUrl = publicSubmissionToken
    ? `${window.location.origin}/submit/${publicSubmissionToken}`
    : null;

  const handleCopy = async () => {
    if (!submissionUrl) return;
    await navigator.clipboard.writeText(submissionUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSubmissions = async (pageNumber: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await getPendingSubmissions(pageNumber);
      if (pageNumber === 1) {
        setItems(data.items);
      } else {
        setItems((prev) => [...prev, ...data.items]);
      }
      setTotalCount(data.totalCount);
      setPage(pageNumber);
    } catch {
      setError("Failed to load pending submissions.");
    } finally {
      setLoading(false);
    }
  };

  // Re-pull however far the user had loaded before navigating away, so "Load more"
  // progress isn't lost. Runs once on mount.
  useEffect(() => {
    // Read the depth before anything else — the persist effect below runs on this same
    // mount with page still 1, which clears the stored value.
    const target = readStoredPage();
    const restoreThroughStoredPage = async () => {
      for (let pageNumber = 1; pageNumber <= target; pageNumber++) {
        await loadSubmissions(pageNumber);
      }
      setIsRestored(true);
    };
    restoreThroughStoredPage();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", String(page));
    saveCrmViewState(pendingSubmissionsViewKey(), params);
  }, [page]);

  // Wait for the restore loop to finish — restoring the offset against a partially
  // loaded list would clamp it to the shorter content.
  useCrmScrollRestoration(
    pendingSubmissionsScrollKey(),
    isRestored && !loading && items.length > 0
  );

  const handleOpenResolveModal = (item: PendingSubmissionResponse) => {
    setResolvingSubmission(item);
  };

  const handleResolveSubmit = async (payload: ResolveSubmissionPayload) => {
    if (!resolvingSubmission) return;
    const id = resolvingSubmission.id;
    setActionId(id);
    try {
      await resolveSubmission(id, {
        action: "create",
        recruitmentStage: payload.recruitmentStage,
        projectedDivisions: payload.projectedDivisions,
        notes: payload.notes,
      });
      setItems((prev) => prev.filter((s) => s.id !== id));
      setTotalCount((c) => c - 1);
      if (expandedId === id) setExpandedId(null);
      setResolvingSubmission(null);
    } catch (err) {
      setError("Failed to resolve submission.");
      throw err instanceof Error ? err : new Error("Failed to resolve submission.");
    } finally {
      setActionId(null);
    }
  };

  // TODO: Re-enable merge button when merge flow is built out
  // const handleMerge = async (id: string, profileId: string) => {
  //   setActionId(id);
  //   try {
  //     await resolveSubmission(id, { action: "merge", mergeWithProspectId: profileId });
  //     setItems((prev) => prev.filter((s) => s.id !== id));
  //     setTotalCount((c) => c - 1);
  //     if (expandedId === id) setExpandedId(null);
  //   } catch { setError("Failed to merge submission."); }
  //   finally { setActionId(null); }
  // };

  const handleDismiss = async (id: string) => {
    setActionId(id);
    try {
      await dismissSubmission(id);
      setItems((prev) => prev.filter((s) => s.id !== id));
      setTotalCount((c) => c - 1);
      if (expandedId === id) setExpandedId(null);
    } catch {
      setError("Failed to dismiss submission.");
    } finally {
      setActionId(null);
    }
  };

  const renderDetailRow = (label: string, value?: string | number | null) => {
    if (value == null || value === "") return null;
    return (
      <div className="pending-submission__detail-row">
        <span className="pending-submission__detail-label">{label}</span>
        <span className="pending-submission__detail-value">{value}</span>
      </div>
    );
  };

  const renderParent = (parent: SubmittedParent | undefined, label: string) => {
    if (!parent?.firstName) return null;
    return (
      <div className="pending-submission__detail-parent">
        <span className="pending-submission__detail-parent-title">{label}</span>
        <div className="pending-submission__detail-parent-fields">
          {renderDetailRow("Name", `${parent.firstName} ${parent.lastName || ""}`.trim())}
          {renderDetailRow("Email", parent.email)}
          {renderDetailRow("Phone", formatPhone(parent.phone))}
          {renderDetailRow("X", parent.xHandle)}
          {renderDetailRow("Slack", parent.slackUsername)}
        </div>
      </div>
    );
  };

  return (
    <div className="pending-submissions">
      <div className="pending-submissions__header">
        <h2 className="pending-submissions__title">Pending Submissions</h2>
        <p className="pending-submissions__subtitle">
          Player interest form submissions waiting for review.
          {totalCount > 0 && ` ${totalCount} pending.`}
        </p>
      </div>

      {submissionUrl && (
        <div className="pending-submissions__url-bar">
          <span className="pending-submissions__url-label" id="pending-submissions-page-player-intake-form-url-0">Player Intake Form URL</span>
          <div className="pending-submissions__url-row" role="group" aria-labelledby="pending-submissions-page-player-intake-form-url-0">
            <input
              className="pending-submissions__url-input"
              type="text"
              value={submissionUrl}
              readOnly
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              className="pending-submissions__url-copy"
              onClick={handleCopy}
              type="button"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {error && <div className="pending-submissions__error">{error}</div>}

      {loading && page === 1 ? (
        <div className="pending-submissions__loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="pending-submissions__empty">
          No pending submissions.
        </div>
      ) : (
        <>
          <div className="pending-submissions__list">
            {items.map((item) => {
              const data = parseSubmittedData(item.submittedData);
              const busy = actionId === item.id;
              const isExpanded = expandedId === item.id;
              return (
                <div key={item.id} className={`pending-submission${isExpanded ? " pending-submission--expanded" : ""}`}>
                  <div
                    className="pending-submission__summary"
                    {...activate(() => setExpandedId(isExpanded ? null : item.id))}
                  >
                    <div className="pending-submission__info">
                      <span className="pending-submission__name">
                        {data.firstName} {data.lastName}
                      </span>
                      <div className="pending-submission__details">
                        {data.gradYear && <span>{data.gradYear}</span>}
                        {data.position && <span>{data.position}</span>}
                        {data.email && <span>{data.email}</span>}
                        {formatPhone(data.phone) && <span>{formatPhone(data.phone)}</span>}
                      </div>
                      {item.potentialMatchId && (
                        <div className="pending-submission__match">
                          Possible duplicate: {item.potentialMatchName} (
                          {Math.round(item.potentialMatchConfidence * 100)}%
                          confidence)
                        </div>
                      )}
                      <div className="pending-submission__date">
                        Submitted {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="pending-submission__actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="pending-submission__btn pending-submission__btn--create"
                        onClick={() => handleOpenResolveModal(item)}
                        disabled={busy}
                      >
                        Create New
                      </button>
                      <button
                        className="pending-submission__btn pending-submission__btn--dismiss"
                        onClick={() => handleDismiss(item.id)}
                        disabled={busy}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="pending-submission__expanded">
                      <div className="pending-submission__detail-section">
                        <h4 className="pending-submission__detail-section-title">Personal Information</h4>
                        <div className="pending-submission__detail-grid">
                          {renderDetailRow("Email", data.email)}
                          {renderDetailRow("Phone", formatPhone(data.phone))}
                        </div>
                      </div>

                      {(data.position || data.bats || data.throws || data.height || data.weight) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Player Details</h4>
                          <div className="pending-submission__detail-grid">
                            {renderDetailRow("Position", data.position)}
                            {renderDetailRow("Bats", data.bats)}
                            {renderDetailRow("Throws", data.throws)}
                            {renderDetailRow("Height", data.height)}
                            {renderDetailRow("Weight", data.weight != null ? `${data.weight} lbs` : undefined)}
                          </div>
                        </div>
                      )}

                      {(data.school || data.gradYear || data.city || data.state || data.gpa || data.intendedMajor || (data.preferredLocations && data.preferredLocations.length > 0)) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Academic & Location</h4>
                          <div className="pending-submission__detail-grid">
                            {renderDetailRow("School", data.school)}
                            {renderDetailRow("Class", data.gradYear)}
                            {renderDetailRow("City", data.city)}
                            {renderDetailRow("State", data.state)}
                            {renderDetailRow("GPA", data.gpa)}
                            {renderDetailRow("Intended Major", data.intendedMajor)}
                            {data.preferredLocations && data.preferredLocations.length > 0 &&
                              renderDetailRow("Preferred Locations", data.preferredLocations.join(", "))}
                          </div>
                        </div>
                      )}

                      {(data.videoLink || data.scoutingProfileLink) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Links</h4>
                          <div className="pending-submission__detail-grid">
                            {renderDetailRow("Video Link", data.videoLink)}
                            {renderDetailRow("Scouting Profile", data.scoutingProfileLink)}
                          </div>
                        </div>
                      )}

                      {(data.travelTeam || data.travelTeamCoachName || data.travelTeamCoachPhone) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Travel Team</h4>
                          <div className="pending-submission__detail-grid">
                            {renderDetailRow("Team", data.travelTeam)}
                            {renderDetailRow("Coach Name", data.travelTeamCoachName)}
                            {renderDetailRow("Coach Phone", formatPhone(data.travelTeamCoachPhone))}
                          </div>
                        </div>
                      )}

                      {(data.endGoal || data.recruitmentStage) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Goals</h4>
                          <div className="pending-submission__detail-grid">
                            {renderDetailRow("End Goal", data.endGoal)}
                            {renderDetailRow("Recruitment Stage", data.recruitmentStage)}
                          </div>
                        </div>
                      )}

                      {(data.parent1?.firstName || data.parent2?.firstName) && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Parents / Guardians</h4>
                          {renderParent(data.parent1, "Parent 1")}
                          {renderParent(data.parent2, "Parent 2")}
                        </div>
                      )}

                      {data.notes && (
                        <div className="pending-submission__detail-section">
                          <h4 className="pending-submission__detail-section-title">Notes</h4>
                          <p className="pending-submission__detail-notes">{data.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {items.length < totalCount && (
            <button
              className="pending-submissions__load-more"
              onClick={() => loadSubmissions(page + 1)}
              disabled={loading}
            >
              {loading
                ? "Loading..."
                : `Load more (${totalCount - items.length} remaining)`}
            </button>
          )}
        </>
      )}

      <ResolveSubmissionModal
        isOpen={!!resolvingSubmission}
        playerName={(() => {
          if (!resolvingSubmission) return "";
          const data = parseSubmittedData(resolvingSubmission.submittedData);
          return `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim() || "Unnamed player";
        })()}
        saving={!!resolvingSubmission && actionId === resolvingSubmission.id}
        onSubmit={handleResolveSubmit}
        onClose={() => {
          if (actionId) return;
          setResolvingSubmission(null);
        }}
      />
    </div>
  );
};
